import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import client from '../api/client';

export const LoginPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [form, setForm] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'A valid email address is required.';
    if (!form.password.trim()) e.password = 'Password is required.';
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setIsLoading(true);
    try {
      const response = await client.post('/auth/login', {
        email: form.email,
        password: form.password,
      });
      // Store token in localStorage for use in Authorization header
      if (response.data.token) {
        localStorage.setItem('rdf_token', response.data.token);
      }
      await login();
      navigate('/portal');
    } catch (err: any) {
      setErrors({
        submit: err.response?.data?.error || 'Login failed. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const field = (id: keyof typeof form) => ({
    value: form[id],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm(f => ({ ...f, [id]: e.target.value }));
      setErrors(er => ({ ...er, [id]: '' }));
    },
  });

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: 400, margin: '0 auto' }}>
        <div className="auth-logo">
          <img src="/RDF_Logo.svg" alt="RDF" className="auth-logo-img" />
          <span className="auth-logo-text">Retail Derivatives Forum</span>
        </div>
        <h2 className="auth-title">Sign In</h2>
        <p className="auth-subtitle">
          Access your member account to view documents, best practices, and regulatory discussions.
        </p>

        {errors.submit && <div className="alert alert-error">{errors.submit}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label className="form-label">Email Address <span className="req">*</span></label>
            <input
              className={`form-input ${errors.email ? 'is-error' : ''}`}
              type="email"
              {...field('email')}
              placeholder="jane@firm.com"
              autoComplete="email"
            />
            {errors.email && <div className="form-error">{errors.email}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Password <span className="req">*</span></label>
            <input
              className={`form-input ${errors.password ? 'is-error' : ''}`}
              type="password"
              {...field('password')}
              placeholder="Enter your password"
              autoComplete="current-password"
            />
            {errors.password && <div className="form-error">{errors.password}</div>}
            <div style={{ textAlign: 'right', marginTop: 8 }}>
              <span onClick={() => navigate('/forgot-password')} style={{ fontSize: 12, color: 'var(--c-primary)', cursor: 'pointer', textDecoration: 'underline' }}>
                Forgot password?
              </span>
            </div>
          </div>

          <button className="btn btn-primary btn-full" type="submit" disabled={isLoading}>
            {isLoading ? 'Signing In…' : 'Sign In'}
          </button>
        </form>

        <div className="auth-switch" style={{ marginTop: 16 }}>
          Don't have an account? <span onClick={() => navigate('/register')}>Register</span>
        </div>
      </div>
    </div>
  );
};
